import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { invoiceAPI } from '../api/api';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  FileText, 
  Plus, 
  DownloadSimple, 
  PaperPlaneTilt, 
  CheckCircle, 
  Clock, 
  Warning,
  CurrencyDollar,
  MagnifyingGlass,
  Funnel,
  Eye,
  Trash,
  XCircle,
  SpinnerGap,
  ArrowRight
} from '@phosphor-icons/react';
import { toast } from '../hooks/use-toast';
import { BreadcrumbsInline } from '../components/Breadcrumbs';

const statusConfig = {
  draft: { label: 'Draft', color: 'bg-neutral-100 dark:bg-neutral-700 text-neutral-700 dark:text-neutral-300 border-neutral-300 dark:border-neutral-600', icon: FileText },
  sent: { label: 'Sent', color: 'bg-[#2B4C6F]/10 text-[#2B4C6F] border-[#2B4C6F]', icon: PaperPlaneTilt },
  paid: { label: 'Paid', color: 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 border-emerald-400', icon: CheckCircle },
  overdue: { label: 'Overdue', color: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 border-red-400', icon: Warning },
  cancelled: { label: 'Cancelled', color: 'bg-neutral-100 dark:bg-neutral-800 text-neutral-500 dark:text-neutral-500 border-neutral-300 dark:border-neutral-700', icon: XCircle },
};

const Invoices = () => {
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [userType, setUserType] = useState(null);

  useEffect(() => {
    const storedUserType = localStorage.getItem('userType');
    setUserType(storedUserType);
    fetchInvoices();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional mount-only effect
  }, [filterStatus]);

  const fetchInvoices = async () => {
    try {
      const response = await invoiceAPI.getInvoices(filterStatus || undefined);
      setInvoices(response.data.invoices || []);
    } catch (error) {
      console.error('Error fetching invoices:', error);
      toast({
        title: 'Error',
        description: 'Failed to load invoices',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPDF = async (invoiceId, invoiceNumber) => {
    try {
      const response = await invoiceAPI.downloadPDF(invoiceId);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `invoice_${invoiceNumber}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
      
      toast({
        title: 'PDF Downloaded',
        description: `Invoice ${invoiceNumber} downloaded successfully`,
      });
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast({
        title: 'Download Failed',
        description: 'Could not download the invoice PDF',
        variant: 'destructive',
      });
    }
  };

  const handleSendInvoice = async (invoiceId) => {
    try {
      await invoiceAPI.sendInvoice(invoiceId);
      toast({
        title: 'Invoice Sent',
        description: 'The invoice has been sent to the contractor',
      });
      fetchInvoices();
    } catch (error) {
      console.error('Error sending invoice:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to send invoice',
        variant: 'destructive',
      });
    }
  };

  const handleMarkPaid = async (invoiceId) => {
    try {
      await invoiceAPI.markPaid(invoiceId);
      toast({
        title: 'Invoice Marked as Paid',
        description: 'The invoice status has been updated',
      });
      fetchInvoices();
    } catch (error) {
      console.error('Error marking invoice paid:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to update invoice',
        variant: 'destructive',
      });
    }
  };

  const handleDeleteInvoice = async (invoiceId) => {
    if (!window.confirm('Are you sure you want to delete this draft invoice?')) return;
    
    try {
      await invoiceAPI.deleteInvoice(invoiceId);
      toast({
        title: 'Invoice Deleted',
        description: 'The draft invoice has been deleted',
      });
      fetchInvoices();
    } catch (error) {
      console.error('Error deleting invoice:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to delete invoice',
        variant: 'destructive',
      });
    }
  };

  const filteredInvoices = invoices.filter(invoice => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      invoice.invoice_number.toLowerCase().includes(query) ||
      invoice.contractor_name?.toLowerCase().includes(query) ||
      invoice.worker_name?.toLowerCase().includes(query) ||
      invoice.job_title?.toLowerCase().includes(query)
    );
  });

  const stats = {
    total: invoices.length,
    draft: invoices.filter(i => i.status === 'draft').length,
    sent: invoices.filter(i => i.status === 'sent').length,
    paid: invoices.filter(i => i.status === 'paid').length,
    totalAmount: invoices.reduce((sum, i) => sum + (i.total || 0), 0),
    paidAmount: invoices.filter(i => i.status === 'paid').reduce((sum, i) => sum + (i.total || 0), 0),
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F0F0F2] dark:bg-neutral-900 flex items-center justify-center">
        <SpinnerGap size={48} weight="bold" className="text-[#2B4C6F] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F0F0F2] dark:bg-neutral-900 transition-colors duration-300" data-testid="invoices-page">
      {/* Header */}
      <div className="bg-[#2B4C6F] text-white py-6 border-b-4 border-black dark:border-[#2B4C6F]">
        <div className="max-w-7xl mx-auto px-4">
          <BreadcrumbsInline 
            items={[
              { label: 'Dashboard', href: userType === 'worker' ? '/worker-dashboard' : '/contractor-dashboard' },
              { label: 'Invoices' }
            ]}
            className="mb-2"
          />
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-3xl md:text-4xl font-bold flex items-center gap-3">
                <FileText size={36} weight="bold" />
                Invoices
              </h1>
              <p className="text-blue-100 text-lg mt-1">
                {userType === 'worker' ? 'Manage your invoices and get paid' : 'View invoices from workers'}
              </p>
            </div>
            {userType === 'worker' && (
              <div className="flex flex-wrap gap-3">
                <button 
                  onClick={() => navigate('/invoices/templates')}
                  className="flex items-center gap-2 h-12 px-4 bg-white/20 text-white font-bold rounded-xl border-2 border-white/30 hover:bg-white/30 transition-all"
                  data-testid="templates-btn"
                >
                  <FileText size={20} weight="bold" />
                  Templates
                </button>
                <button 
                  onClick={() => navigate('/invoices/recurring')}
                  className="flex items-center gap-2 h-12 px-4 bg-white/20 text-white font-bold rounded-xl border-2 border-white/30 hover:bg-white/30 transition-all"
                  data-testid="recurring-btn"
                >
                  <Clock size={20} weight="bold" />
                  Recurring
                </button>
                <button 
                  onClick={() => navigate('/invoices/create')}
                  className="flex items-center gap-2 h-12 px-5 bg-white text-[#2B4C6F] font-bold rounded-xl border-2 border-black shadow-[3px_3px_0px_#000] hover:shadow-[4px_4px_0px_#000] active:translate-y-1 active:shadow-none transition-all"
                  data-testid="create-invoice-btn"
                >
                  <Plus size={20} weight="bold" />
                  Create Invoice
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white dark:bg-neutral-800 border-2 border-black dark:border-neutral-700 rounded-xl p-5 shadow-[4px_4px_0px_#000] dark:shadow-[4px_4px_0px_#2B4C6F]">
            <FileText size={28} weight="bold" className="text-neutral-500 dark:text-neutral-400 mb-2" />
            <p className="text-sm font-bold text-neutral-500 dark:text-neutral-400">Total Invoices</p>
            <p className="text-3xl font-bold text-neutral-900 dark:text-white">{stats.total}</p>
          </div>
          <div className="bg-white dark:bg-neutral-800 border-2 border-black dark:border-neutral-700 rounded-xl p-5 shadow-[4px_4px_0px_#000] dark:shadow-[4px_4px_0px_#2B4C6F]">
            <Clock size={28} weight="bold" className="text-[#2B4C6F] mb-2" />
            <p className="text-sm font-bold text-neutral-500 dark:text-neutral-400">Pending</p>
            <p className="text-3xl font-bold text-[#2B4C6F]">{stats.draft + stats.sent}</p>
          </div>
          <div className="bg-white dark:bg-neutral-800 border-2 border-black dark:border-neutral-700 rounded-xl p-5 shadow-[4px_4px_0px_#000] dark:shadow-[4px_4px_0px_#2B4C6F]">
            <CheckCircle size={28} weight="bold" className="text-emerald-600 mb-2" />
            <p className="text-sm font-bold text-neutral-500 dark:text-neutral-400">Paid</p>
            <p className="text-3xl font-bold text-emerald-600">{stats.paid}</p>
          </div>
          <div className="bg-white dark:bg-neutral-800 border-2 border-black dark:border-neutral-700 rounded-xl p-5 shadow-[4px_4px_0px_#000] dark:shadow-[4px_4px_0px_#2B4C6F]">
            <CurrencyDollar size={28} weight="bold" className="text-emerald-600 mb-2" />
            <p className="text-sm font-bold text-neutral-500 dark:text-neutral-400">Total Earned</p>
            <p className="text-3xl font-bold text-emerald-600">${stats.paidAmount.toLocaleString()}</p>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white dark:bg-neutral-800 border-2 border-black dark:border-neutral-700 rounded-xl p-4 mb-6 shadow-[4px_4px_0px_#000] dark:shadow-[4px_4px_0px_#2B4C6F]">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <MagnifyingGlass size={24} weight="bold" className="absolute left-4 top-1/2 transform -translate-y-1/2 text-neutral-400" />
              <input
                type="text"
                placeholder="Search invoices..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full h-12 pl-12 pr-4 border-2 border-neutral-300 dark:border-neutral-600 rounded-xl bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white focus:outline-none focus:border-[#2B4C6F] font-medium"
                data-testid="search-invoices-input"
              />
            </div>
            <div className="flex items-center gap-2">
              <Funnel size={20} weight="bold" className="text-neutral-400" />
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="h-12 px-4 border-2 border-neutral-300 dark:border-neutral-600 rounded-xl bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white font-medium"
                data-testid="filter-status-select"
              >
                <option value="">All Status</option>
                <option value="draft">Draft</option>
                <option value="sent">Sent</option>
                <option value="paid">Paid</option>
                <option value="overdue">Overdue</option>
              </select>
            </div>
          </div>
        </div>

        {/* Invoice List */}
        {filteredInvoices.length === 0 ? (
          <div className="bg-white dark:bg-neutral-800 border-2 border-black dark:border-neutral-700 rounded-xl p-12 text-center shadow-[4px_4px_0px_#000] dark:shadow-[4px_4px_0px_#2B4C6F]">
            <FileText size={64} weight="bold" className="text-neutral-300 dark:text-neutral-600 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-neutral-900 dark:text-white mb-2">No Invoices Found</h3>
            <p className="text-neutral-500 dark:text-neutral-400 mb-6">
              {userType === 'worker' 
                ? "You haven't created any invoices yet. Create your first invoice to get paid!"
                : "No invoices have been sent to you yet."
              }
            </p>
            {userType === 'worker' && (
              <button 
                onClick={() => navigate('/invoices/create')}
                className="inline-flex items-center gap-2 h-14 px-6 bg-[#2B4C6F] text-white font-bold rounded-xl border-2 border-black shadow-[4px_4px_0px_#000] hover:shadow-[5px_5px_0px_#000] active:translate-y-1 active:shadow-none transition-all"
              >
                <Plus size={24} weight="bold" />
                Create Your First Invoice
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            {filteredInvoices.map((invoice) => {
              const StatusIcon = statusConfig[invoice.status]?.icon || FileText;
              return (
                <div 
                  key={invoice._id}
                  className="bg-white dark:bg-neutral-800 border-2 border-black dark:border-neutral-700 rounded-xl p-6 shadow-[4px_4px_0px_#000] dark:shadow-[4px_4px_0px_#2B4C6F] hover:translate-y-[-2px] hover:shadow-[4px_6px_0px_#000] dark:hover:shadow-[4px_6px_0px_#2B4C6F] transition-all"
                  data-testid={`invoice-${invoice._id}`}
                >
                  <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                    {/* Invoice Info */}
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-3">
                        <h3 className="text-lg font-bold text-neutral-900 dark:text-white">
                          {invoice.invoice_number}
                        </h3>
                        <Badge className={`${statusConfig[invoice.status]?.color} border-2 font-bold`}>
                          <StatusIcon size={14} weight="bold" className="mr-1" />
                          {statusConfig[invoice.status]?.label}
                        </Badge>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div>
                          <span className="text-neutral-500 dark:text-neutral-400 font-medium">
                            {userType === 'worker' ? 'Bill To' : 'From'}
                          </span>
                          <p className="font-bold text-neutral-900 dark:text-white">
                            {userType === 'worker' ? invoice.contractor_name : invoice.worker_name}
                          </p>
                        </div>
                        {invoice.job_title && (
                          <div>
                            <span className="text-neutral-500 dark:text-neutral-400 font-medium">Job</span>
                            <p className="font-bold text-neutral-900 dark:text-white truncate">
                              {invoice.job_title}
                            </p>
                          </div>
                        )}
                        <div>
                          <span className="text-neutral-500 dark:text-neutral-400 font-medium">Date</span>
                          <p className="font-bold text-neutral-900 dark:text-white">
                            {new Date(invoice.created_at).toLocaleDateString()}
                          </p>
                        </div>
                        <div>
                          <span className="text-neutral-500 dark:text-neutral-400 font-medium">Amount</span>
                          <p className="font-bold text-xl text-[#2B4C6F]">
                            ${invoice.total?.toFixed(2)}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2 flex-wrap">
                      <button
                        onClick={() => navigate(`/invoices/${invoice._id}`)}
                        className="flex items-center gap-1 h-10 px-4 bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white font-bold rounded-lg border-2 border-black dark:border-neutral-600 shadow-[2px_2px_0px_#000] dark:shadow-[2px_2px_0px_#2B4C6F] active:translate-y-1 active:shadow-none transition-all"
                        data-testid={`view-invoice-${invoice._id}`}
                      >
                        <Eye size={18} weight="bold" />
                        View
                      </button>
                      <button
                        onClick={() => handleDownloadPDF(invoice._id, invoice.invoice_number)}
                        className="flex items-center gap-1 h-10 px-4 bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white font-bold rounded-lg border-2 border-black dark:border-neutral-600 shadow-[2px_2px_0px_#000] dark:shadow-[2px_2px_0px_#2B4C6F] active:translate-y-1 active:shadow-none transition-all"
                        data-testid={`download-invoice-${invoice._id}`}
                      >
                        <DownloadSimple size={18} weight="bold" />
                        PDF
                      </button>
                      {userType === 'worker' && invoice.status === 'draft' && (
                        <>
                          <button
                            onClick={() => handleSendInvoice(invoice._id)}
                            className="flex items-center gap-1 h-10 px-4 bg-[#2B4C6F] text-white font-bold rounded-lg border-2 border-black shadow-[2px_2px_0px_#000] active:translate-y-1 active:shadow-none transition-all"
                            data-testid={`send-invoice-${invoice._id}`}
                          >
                            <PaperPlaneTilt size={18} weight="bold" />
                            Send
                          </button>
                          <button
                            onClick={() => handleDeleteInvoice(invoice._id)}
                            className="flex items-center gap-1 h-10 px-3 bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 font-bold rounded-lg border-2 border-red-300 dark:border-red-700 hover:bg-red-200 dark:hover:bg-red-900/50 transition-all"
                            data-testid={`delete-invoice-${invoice._id}`}
                          >
                            <Trash size={18} weight="bold" />
                          </button>
                        </>
                      )}
                      {invoice.status === 'sent' && (
                        <button
                          onClick={() => handleMarkPaid(invoice._id)}
                          className="flex items-center gap-1 h-10 px-4 bg-emerald-600 text-white font-bold rounded-lg border-2 border-black shadow-[2px_2px_0px_#000] active:translate-y-1 active:shadow-none transition-all"
                          data-testid={`mark-paid-${invoice._id}`}
                        >
                          <CheckCircle size={18} weight="bold" />
                          Mark Paid
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default Invoices;
